import React, { useEffect, useRef, useState } from 'react';
import host from '../../global';
import axios from 'axios';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import Cookies from 'js-cookie';
import { getCurrentUser } from '../../utils/authSession';
import Loader from '../Loader';
import { useTheme } from '../../hooks/useTheme';
import CustomDropdown from '../common/CustomDropdown';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { fetchFirstRunGuide } from '../../store/slices/firstRunGuideSlice';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Finance & Banking', 'Education',
  'Manufacturing', 'Retail & E-Commerce', 'Media & Entertainment',
  'Real Estate', 'Transportation & Logistics', 'Hospitality & Tourism',
  'Construction', 'Energy & Utilities', 'Agriculture',
  'Legal & Consulting', 'Non-Profit', 'Other',
];

const EMPLOYEE_SIZE_OPTIONS = [
  '1–10', '11–50', '51–200', '201–500', '501–1000', '1001–5000', '5000+',
];

const Size = Quill.import('attributors/style/size');
Size.whitelist = ['12px', '14px', '16px', '20px', '26px', '36px'];
Quill.register(Size, true);

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: ['12px', '14px', '16px', '20px', '26px', '36px'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'strike', 'size', 'list', 'bullet', 'link'];

const getInputStyle = (theme) => ({
  width: '100%', padding: '11px 12px', fontSize: '14px',
  border: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'}`,
  borderRadius: '6px',
  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
  color: theme === 'dark' ? '#d1d5db' : '#111827',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  lineHeight: '1.5',
});

const Row = ({ label, hint, children, last, theme }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '220px 1fr',
    gap: '16px 40px',
    padding: '20px 0',
    borderBottom: last ? 'none' : `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#e5e7eb'}`,
    alignItems: 'start',
  }}>
    <div>
      <p style={{ margin: '0 0 3px', lineHeight: '1.4' }}
        className="dark:text-gray-300 text-textPrimary text-sm font-medium">
        {label}
      </p>
      {hint && (
        <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.45' }}
          className="dark:text-[#8893C8] text-textSecondary">
          {hint}
        </p>
      )}
    </div>
    <div>{children}</div>
  </div>
);

const SectionHeader = ({ title, description, theme, first }) => (
  <div style={{ padding: `${first ? '0' : '28px'} 0 4px` }}>
    <h2 style={{ margin: '0 0 4px' }}
      className="dark:text-white text-textPrimary text-lg font-semibold">
      {title}
    </h2>
    {description && (
      <p style={{ margin: 0, fontSize: '13px' }}
        className="dark:text-[#8893C8] text-textSecondary">{description}</p>
    )}
  </div>
);

// The editor saves markup even when empty ("<p><br></p>"), so check the words
const aboutText = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const parseOrgData = (data) => ({
  companyName: data.company_name || '',
  websiteLink: data.website_link || '',
  industry: data.industry || '',
  locations: data.locations || '',
  employeeSize: data.employee_size || '',
  aboutCompany: data.about_company || '',
  hrContact: data.hr_contact || '',
  hrEmail: data.hr_email || '',
});

// onSaved(fields) hands the saved values back to the Settings page, which keeps
// its own copy and would otherwise show stale values when this form remounts.
const EditCompanyProfile = ({ initialData = null, onSaved }) => {
  const dispatch = useDispatch();
  const profile_photos_host = process.env.REACT_APP_PROFILE_PHOTOS_HOST;

  const [profileData, setProfileData] = useState(
    initialData ? parseOrgData(initialData) : {
      companyName: '', websiteLink: '', industry: '',
      locations: '', employeeSize: '', aboutCompany: '',
      hrContact: '', hrEmail: '',
    }
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [websiteError, setWebsiteError] = useState('');
  const [aboutError, setAboutError] = useState('');
  const aboutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [defaultCountry, setDefaultCountry] = useState("IN");
  const { theme } = useTheme();
  const [isFetchingAbout, setIsFetchingAbout] = useState(false);
  const accessToken = Cookies.get('accessToken');

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const { data } = await axios.get("https://ipwho.is/", { timeout: 3000 });
        const code = data.country_code?.toUpperCase();
        if (code) setDefaultCountry(code);
      } catch (e) {
        console.warn("IP geolocation failed, using fallback");
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    if (initialData) return;
    const fetchProfileData = async () => {
      try {
        const res = await fetch(`${host}/getorgdetails`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        setProfileData(parseOrgData(data));
      } catch (err) {
        console.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfileData();
  }, [accessToken, initialData]);

  const isValidWebsite = (url) => {
    if (!url) return true; // optional field
    try {
      const full = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      const { hostname } = new URL(full);
      return hostname.includes('.') && hostname.split('.').every(p => p.length > 0);
    } catch {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', type: '' });

    if (!profileData.websiteLink.trim()) {
      setWebsiteError('Please enter your company website.');
      setIsSubmitting(false);
      return;
    }

    if (!aboutText(profileData.aboutCompany)) {
      setAboutError('Please fill in a short description of your company.');
      aboutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsSubmitting(false);
      return;
    }

    if (profileData.websiteLink && !isValidWebsite(profileData.websiteLink)) {
      toast.error("Please enter a valid website URL");
      setIsSubmitting(false);
      return;
    }

    if (profileData.hrContact && !isValidPhoneNumber(profileData.hrContact)) {
      toast.error("Please enter a valid phone number");
      setIsSubmitting(false);
      return;
    }

    const fd = new FormData();
    fd.append('company_name', profileData.companyName);
    fd.append('website_link', profileData.websiteLink);
    fd.append('industry', profileData.industry);
    fd.append('locations', profileData.locations);
    fd.append('employee_size', profileData.employeeSize);
    fd.append('about_company', profileData.aboutCompany);
    fd.append('hr_contact', profileData.hrContact);
    fd.append('hr_email', profileData.hrEmail);

    try {
      const res = await fetch(`${host}/org-admin/updateprofile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      if (res.ok) {
        toast.success("Profile updated successfully");
        onSaved?.({
          company_name: profileData.companyName,
          website_link: profileData.websiteLink,
          industry: profileData.industry,
          locations: profileData.locations,
          employee_size: profileData.employeeSize,
          about_company: profileData.aboutCompany,
          hr_contact: profileData.hrContact,
          hr_email: profileData.hrEmail,
        });
        // A filled-in profile can complete the setup guide's first step
        dispatch(fetchFirstRunGuide());
      } else {
        const err = await res.json();
        console.log(err);
        toast.error("Failed to Update Profile");
      }
    } catch {
      toast.error("Failed to Update Profile");
    }
    setIsSubmitting(false);
  };

  if (isLoading) return null;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus, select:focus, textarea:focus { outline: none; border-color: #9ca3af !important; }
        .PhoneInput { display: flex; align-items: center; gap: 8px; }
        .PhoneInputInput { flex: 1; background: transparent; border: none; outline: none; font-size: 14px; font-family: inherit; padding: 11px 4px; }
        .PhoneInputInput::placeholder { color: #9ca3af; }
        .PhoneInputCountry { display: flex; align-items: center; }
      `}</style>

      <form id="company-profile-form" className="pr-[150px]" onSubmit={handleSubmit}>

          {/* Basic information */}
          <SectionHeader
            first
            title="Basic information"
            description="This information will be used in all candidate communications and job listings."
          />

          <Row label="Company name" theme={theme}>
            <input
              type="text" name="companyName" value={profileData.companyName}
              onChange={handleChange} required placeholder="e.g. Acme Corp"
              style={getInputStyle(theme)}
            />
          </Row>

          <Row label={<>Website <span className="text-red-500">*</span></>} hint="Include https://" theme={theme}>
            <div
              data-guide="company-website"
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
                <div style={{
                  display: 'flex', borderRadius: '6px', overflow: 'hidden',
                  border: `1px solid ${websiteError ? '#ef4444' : theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'}`,
                }}>
                <span style={{
                    padding: '11px 12px',
                    background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                    fontSize: '13px',
                    borderRight: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'}`,
                    whiteSpace: 'nowrap', lineHeight: '1.5',
                }} className="dark:text-[#8893C8] text-textSecondary">
                    https://
                </span>
                <input
                    type="text" name="websiteLink"
                    value={profileData.websiteLink.replace(/^https?:\/\//, '')}
                    onChange={(e) => {
                      setProfileData(p => ({ ...p, websiteLink: e.target.value }));
                      if (websiteError) setWebsiteError('');
                    }}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val && !isValidWebsite(val)) {
                        setWebsiteError('Please enter a valid website URL (e.g. www.company.com)');
                      } else {
                        setWebsiteError('');
                      }
                    }}
                    placeholder="www.yourcompany.com"
                    style={{ ...getInputStyle(theme), border: 'none', borderRadius: 0, flex: 1 }}
                />
                </div>
                {websiteError && (
                  <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>{websiteError}</p>
                )}
            </div>
            </Row>

          {/* <Row label="Industry" theme={theme}>
            <CustomDropdown
              options={INDUSTRY_OPTIONS}
              value={profileData.industry}
              onChange={(val) => setProfileData(p => ({ ...p, industry: val }))}
              placeholder="Select industry"
              isWhiteLabelingEnable={false}
            />
          </Row> */}

          <Row label="Team size" theme={theme}>
            <CustomDropdown
              options={EMPLOYEE_SIZE_OPTIONS}
              value={profileData.employeeSize}
              onChange={(val) => setProfileData(p => ({ ...p, employeeSize: val }))}
              placeholder="Select range"
              isWhiteLabelingEnable={false}
            />
          </Row>

          <Row label="Locations" hint="Offices or cities where your team is based." theme={theme}>
            <input
              type="text" name="locations" value={profileData.locations}
              onChange={handleChange} placeholder="e.g. London, Seattle, Bangalore"
              style={getInputStyle(theme)}
            />
          </Row>

          {/* About Us */}
          <SectionHeader
            title={<>About Company <span className="text-red-500">*</span></>}
            description="A brief introduction visible to candidates exploring your job listings."
          />

          {/* data-guide: the setup guide's coach mark points here */}
          <div data-guide="company-about">
          {/* Auto-fill button */}
                {profileData.websiteLink && profileData.websiteLink !== 'https://' && (
                <button
                    type="button"
                    disabled={isFetchingAbout}
                    onClick={async () => {
                    setIsFetchingAbout(true);
                    try {
                        const res = await fetch(
                        `${host}/org-admin/fetch-company-about?website=${encodeURIComponent(profileData.websiteLink)}`,
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                        );
                        const data = await res.json();
                        if (res.ok && data.about) {
                        // Convert plain text with newlines and bullets to HTML for Quill
                        const formatted = data.about
                            .split('\n')
                            .map(line => {
                            const trimmed = line.trim();
                            if (!trimmed) return '';
                            if (trimmed.startsWith('- ')) return `<li>${trimmed.slice(2)}</li>`;
                            return `<p>${trimmed}</p>`;
                            })
                            .join('')
                            .replace(/(<li>.*<\/li>)+/g, (match) => `<ul>${match}</ul>`);

                        setProfileData(p => ({ ...p, aboutCompany: formatted }));
                        // toast.success("About Us auto-filled from website");
                        } else {
                        toast.error("Unable to fetch company details. Please enter a valid company website URL.");
                        }
                    } catch {
                        toast.error("Failed to fetch company details");
                    } finally {
                        setIsFetchingAbout(false);
                    }
                    }}
                    style={{
                    alignSelf: 'flex-start',
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '20px',
                    border: 'none',
                    background: isFetchingAbout ? '#6b7280' : 'linear-gradient(to right, #FB54AE, #2F64FF)',
                    color: '#fff',
                    cursor: isFetchingAbout ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '5px',
                    }}
                >
                    {isFetchingAbout && (
                    <span style={{
                        width: '10px', height: '10px', borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                        display: 'inline-block', animation: 'spin 0.7s linear infinite',
                    }} />
                    )}
                    {isFetchingAbout ? 'Fetching...' : '✦ Generate with AI'}
                </button>
                )}

          <div style={{
            padding: '20px 0',
            borderBottom: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#e5e7eb'}`,
            }}>
            <style>{`
              .quill-custom .ql-toolbar {
                border-radius: 6px 6px 0 0;
                border-color: ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'} !important;
                background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb'};
              }
              .quill-custom .ql-container {
                border-radius: 0 0 6px 6px;
                border-color: ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'} !important;
                background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff'};
                font-size: 14px;
                min-height: 180px;
                font-family: inherit;
              }
              .quill-custom .ql-editor {
                min-height: 180px;
                color: ${theme === 'dark' ? '#d1d5db' : '#111827'};
                line-height: 1.6;
              }
              /* Required and left empty on save */
              .quill-custom.quill-error .ql-toolbar,
              .quill-custom.quill-error .ql-container { border-color: #ef4444 !important; }
              .quill-custom .ql-editor.ql-blank::before {
                color: #9ca3af;
                font-style: normal;
              }

              /* ── Icon colors ── */
              .quill-custom .ql-stroke { stroke: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
              .quill-custom .ql-fill   { fill:   ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
              .quill-custom .ql-picker  { color:  ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }

              /* ── Active / selected state ── */
              .quill-custom .ql-toolbar button.ql-active .ql-stroke,
              .quill-custom .ql-toolbar button:hover .ql-stroke { stroke: #2F64FF !important; }
              .quill-custom .ql-toolbar button.ql-active .ql-fill,
              .quill-custom .ql-toolbar button:hover .ql-fill   { fill: #2F64FF !important; }
              .quill-custom .ql-toolbar button.ql-active,
              .quill-custom .ql-toolbar button:hover {
                background: ${theme === 'dark' ? 'rgba(47,100,255,0.15)' : 'rgba(47,100,255,0.08)'} !important;
                border-radius: 4px;
              }
              /* Active picker (size dropdown) */
              .quill-custom .ql-toolbar .ql-picker-label.ql-active,
              .quill-custom .ql-toolbar .ql-picker-label:hover { color: #2F64FF !important; }
              .quill-custom .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
              .quill-custom .ql-toolbar .ql-picker-label:hover .ql-stroke { stroke: #2F64FF !important; }

              /* ── Size picker dropdown ── */
              /* Replace your existing size picker CSS with this */
            .quill-custom .ql-size .ql-picker-label::before,
            .quill-custom .ql-size .ql-picker-item::before { content: 'Normal'; }

            .quill-custom .ql-size .ql-picker-label[data-value="12px"]::before,
            .quill-custom .ql-size .ql-picker-item[data-value="12px"]::before { content: 'Small'; }

            .quill-custom .ql-size .ql-picker-label[data-value="14px"]::before,
            .quill-custom .ql-size .ql-picker-item[data-value="14px"]::before { content: 'Normal'; }

            .quill-custom .ql-size .ql-picker-label[data-value="16px"]::before,
            .quill-custom .ql-size .ql-picker-item[data-value="16px"]::before { content: 'Medium'; }

            .quill-custom .ql-size .ql-picker-item[data-value="20px"]::before,
            .quill-custom .ql-size .ql-picker-label[data-value="20px"]::before { content: 'Large'; }

            .quill-custom .ql-size .ql-picker-item[data-value="26px"]::before,
            .quill-custom .ql-size .ql-picker-label[data-value="26px"]::before { content: 'X-Large'; }

            .quill-custom .ql-size .ql-picker-item[data-value="36px"]::before,
            .quill-custom .ql-size .ql-picker-label[data-value="36px"]::before { content: 'Huge'; }

            .quill-custom .ql-size .ql-picker-options {
              max-height: 220px;
              overflow-y: auto;
              z-index: 9999 !important;
              position: absolute !important;
            }

              /* ── Link tooltip / input ── */
              .quill-custom .ql-tooltip {
                background: ${theme === 'dark' ? '#1e2433' : '#fff'} !important;
                border: 1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.6)' : '#d1d5db'} !important;
                border-radius: 6px !important;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                color: ${theme === 'dark' ? '#d1d5db' : '#111827'} !important;
              }
              .quill-custom .ql-tooltip input[type=text] {
                background: ${theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#f9fafb'} !important;
                border: 1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.6)' : '#d1d5db'} !important;
                border-radius: 4px !important;
                color: ${theme === 'dark' ? '#d1d5db' : '#111827'} !important;
                font-size: 13px !important;
                padding: 4px 8px !important;
                outline: none !important;
              }
              .quill-custom .ql-tooltip input[type=text]::placeholder {
                color: #9ca3af !important;
              }
              .quill-custom .ql-tooltip a.ql-action,
              .quill-custom .ql-tooltip a.ql-remove,
              .quill-custom .ql-tooltip a       { color: #2F64FF !important; }
              .quill-custom .ql-tooltip::before { color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }

              /* ── Toolbar button tooltips ── */
              .quill-custom .ql-toolbar button { position: relative; }
              .quill-custom .ql-toolbar button[class]::after {
                content: attr(data-tooltip);
                position: absolute;
                bottom: calc(100% + 6px);
                left: 50%;
                transform: translateX(-50%);
                background: ${theme === 'dark' ? '#374151' : '#1f2937'};
                color: #fff;
                font-size: 11px;
                padding: 3px 7px;
                border-radius: 4px;
                white-space: nowrap;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.15s;
                z-index: 10;
              }
              .quill-custom .ql-toolbar button:hover::after { opacity: 1; }

              /* ── Quill's own title attributes as tooltips via CSS ── */
              .quill-custom .ql-bold::after      { content: 'Bold' !important; }
              .quill-custom .ql-italic::after    { content: 'Italic' !important; }
              .quill-custom .ql-underline::after { content: 'Underline' !important; }
              .quill-custom .ql-strike::after    { content: 'Strikethrough' !important; }
              .quill-custom .ql-link::after      { content: 'Insert link' !important; }
              .quill-custom .ql-clean::after     { content: 'Remove formatting' !important; }
              .quill-custom .ql-list[value="ordered"]::after  { content: 'Numbered list' !important; }
              .quill-custom .ql-list[value="bullet"]::after   { content: 'Bullet list' !important; }
            `}</style>

            <div ref={aboutRef}>
            <ReactQuill
                className={`quill-custom ${aboutError ? 'quill-error' : ''}`}
                theme="snow"
                value={profileData.aboutCompany}
                onChange={(val) => {
                  setProfileData(p => ({ ...p, aboutCompany: val }));
                  if (aboutError && aboutText(val)) setAboutError('');
                }}
                placeholder="Describe your culture, mission, and what makes your company a great place to work…"
                formats={QUILL_FORMATS}
                modules={QUILL_MODULES}
            />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', margin: '6px 0 0' }}>
              <p role={aboutError ? 'alert' : undefined} style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>
                {aboutError}
              </p>
              <p style={{ margin: 0, fontSize: '12px', textAlign: 'right', flexShrink: 0 }}
                  className="dark:text-gray-500 text-gray-400">
                  {profileData.aboutCompany.replace(/<[^>]*>/g, '').length} characters
              </p>
            </div>
            </div>
          </div>

          {/* HR Contact */}
          <SectionHeader
            title="HR Contact and Support"
            description="Used in candidate communications and for role-related queries and support."
          />

          <div>
            <Row label="Contact Number" last theme={theme}>
              <PhoneInput
                international
                defaultCountry={defaultCountry}
                value={profileData.hrContact}
                onChange={(val) => setProfileData(p => ({ ...p, hrContact: val || '' }))}
                placeholder="+91 98765 43210"
                style={{
                  ...getInputStyle(theme),
                  display: 'flex', alignItems: 'center',
                  paddingLeft: '12px', paddingTop: 0, paddingBottom: 0, paddingRight: 0,
                }}
                countryCallingCodeEditable={true}
                countrySelectComponent={({ value, iconComponent: Icon }) => (
                  <div className="pointer-events-none flex items-center mr-1">
                    {Icon && <Icon country={value} />}
                  </div>
                )}
              />
            </Row>

            <Row label="Email ID" last theme={theme}>
              <div style={{
                ...getInputStyle(theme),
                display: 'flex', alignItems: 'center', gap: '10px',
                paddingTop: 0, paddingBottom: 0,
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'dark' ? '#6b7280' : '#9ca3af'} strokeWidth="1.8" style={{ flexShrink: 0 }}>
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M2 7l10 7 10-7" />
                </svg>
                <input
                  type="email" name="hrEmail" value={profileData.hrEmail}
                  onChange={handleChange} placeholder="hr@example.com"
                  style={{
                    flex: 1, border: 'none', background: 'transparent', outline: 'none',
                    fontSize: '14px', fontFamily: 'inherit',
                    color: theme === 'dark' ? '#d1d5db' : '#111827',
                  }}
                />
              </div>
            </Row>
          </div>

          {/* Status message */}
          {message.text && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '6px', margin: '16px 0',
              background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
            }}>
              {message.type === 'success'
                ? <CheckCircle size={14} color="#16a34a" />
                : <AlertCircle size={14} color="#dc2626" />}
              <p style={{ margin: 0, fontSize: '13px', color: message.type === 'success' ? '#15803d' : '#dc2626' }}>
                {message.text}
              </p>
            </div>
          )}

        </form>
    </>
  );
};

export default EditCompanyProfile;